import {
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  model,
  modelConfig,
  onWrite,
  reference,
} from "@mixitone/oom";
import { default as Tournament, default as TournamentCompetition } from "./TournamentCompetition";
import { Database } from "./database.types";
import { setAccountId } from "./concerns/SetAccountId";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    club_id: {
      type: "string",
    },
    completed_at: {
      type: "datetime",
    },
    court_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    entries: {
      type: "json",
      jsonType: {
        current: [],
        winners: [],
        losers: [],
        score: {},
      } as {
        current: Array<string | undefined>;
        winners: string[];
        losers: string[];
        score: Record<any, any>;
        locked?: boolean;
      },
    },
    format_data: {
      type: "json",
      jsonType: {} as Record<string, any>,
    },
    is_deleted: {
      type: "boolean",
    },
    match_index: {
      type: "number",
    },
    round: {
      type: "number",
    },
    started_at: {
      type: "datetime",
    },
    status: {
      type: "enum",
      enumType: "" as Database["public"]["Enums"]["tournament_match_status"],
    },
    tournament_competition_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
  },
  {},
  {
    competition: reference(() => Tournament as any, "tournament_competition_id"),
    nextWinnerMatch: reference(() => TournamentMatch as any, "next_winner_match_id"),
    nextLoserMatch: reference(() => TournamentMatch as any, "next_loser_match_id"),
  },
);

interface TournamentMatch extends ModelAttributes<typeof config> {
  competition: Loadable<TournamentCompetition>;
  nextWinnerMatch: Loadable<TournamentMatch>;
  nextLoserMatch: Loadable<TournamentMatch>;
}

@setAccountId
@model("tournament_matches", config)
class TournamentMatch extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament match represents a game between entries in a competition." +
      "Match entries are listed in the 'entries.current' array. " +
      "The entries field also specifies the winners and losers and score after a match is finished." +
      "Matches progress through statuses: 'pending' (not ready), 'ready' (can start), 'in_progress' (currently happening), 'completed' (finished), and 'voided' (cancelled without play). " +
      "Round is 0 indexed, so add 1 for display.",
    availableFields: [...Object.keys(config.fields), "isCheckedIn", "names", "shortNames"],
    relationships: {
      competition: "tournament_competitions where id equals this tournament_competition_id",
    },
  };
  get isComplete() {
    return this.status === "completed";
  }

  get isVoided() {
    return this.status === "voided";
  }

  /** Finished for scheduling progress: completed with a result, or voided without play. */
  get isResolved() {
    return this.isComplete || this.isVoided;
  }

  get isInProgress() {
    return this.status === "in_progress";
  }

  get isPending() {
    return this.status === "pending";
  }

  get isReady() {
    return this.status === "ready";
  }

  get notComplete() {
    return !this.isComplete && !this.isVoided;
  }

  get notStarted() {
    return this.isPending || this.isReady;
  }

  get started() {
    return !this.notStarted;
  }

  get hasCourt() {
    return (this.isReady || this.isInProgress) && !!this.court_id;
  }

  get canStart() {
    return this.isReady;
  }

  get voidReason(): string | undefined {
    const reason = this.format_data?.void_reason;
    return typeof reason === "string" ? reason : undefined;
  }

  get entryCount() {
    return this.entries?.current.length ?? 0;
  }

  get hasEntries() {
    return this.entryCount > 1;
  }

  get tournamentEntries() {
    const entryIds = this.entries?.current.filter((id): id is string => !!id) ?? [];
    return this.competition.entries.filter((entry) => entryIds.includes(entry.uniqueId));
  }

  get needsWinners() {
    return this.isComplete && this.entries?.winners.length === 0;
  }

  setEntries(entries: NonNullable<this["entries"]>["current"]) {
    const entryIds = entries.filter((id): id is string => !!id) ?? [];

    this.entries = {
      current: entryIds,
      winners: [],
      losers: [],
      score: {},
    };
  }

  removeEntry(entryId: string) {
    const entryIds = (this.entries?.current || []).filter((id) => id !== entryId);
    return this.setEntries(entryIds);
  }

  setEntry(entryId: string, index: number) {
    this.removeEntry(entryId);
    const entryIds = this.entries?.current || [];
    entryIds[index] = entryId;
    return this.setEntries(entryIds);
  }

  clearEntries() {
    this.entries = {
      current: [],
      winners: [],
      losers: [],
      score: {},
    };
  }

  lockEntries() {
    this.entries = {
      current: this.entries?.current ?? [],
      winners: this.entries?.winners ?? [],
      losers: this.entries?.losers ?? [],
      score: this.entries?.score ?? {},
      locked: true,
    };
  }

  unlockEntries() {
    this.entries = {
      current: this.entries?.current ?? [],
      winners: this.entries?.winners ?? [],
      losers: this.entries?.losers ?? [],
      score: this.entries?.score ?? {},
    };
  }

  get matchEntriesLocked() {
    return this.entries?.locked ?? false;
  }

  @onWrite("status")
  updateTimesForStatusChange(_status: this["status"]) {
    if (this.isComplete) {
      this.completed_at = new Date();
    } else if (this.isInProgress) {
      this.completed_at = undefined;
      this.started_at = new Date();
    }
  }

  async startMatch() {
    if (!this.isReady) return;
    this.status = "in_progress";
    this.started_at = new Date();
    await this.save();
  }

  async finishMatch() {
    if (!this.isInProgress) return;
    this.status = "completed";
    this.completed_at = new Date();
    await this.save();
  }

  get name() {
    return `${this.competition.name} - round ${(this.round ?? 0) + 1}`;
  }
}

export default TournamentMatch;
